import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="section-spacing" data-testid="empty-cart">
        <div className="container-custom">
          <div className="max-w-md mx-auto text-center space-y-6">
            <ShoppingBag className="w-16 h-16 md:w-24 md:h-24 mx-auto text-muted-foreground" />
            <h2 className="text-2xl md:text-3xl font-heading font-bold">Your Cart is Empty</h2>
            <p className="text-sm md:text-lg text-muted-foreground">
              Add some delicious Seedoza energy bites to your cart!
            </p>
            <Link to="/products">
              <button className="btn-primary" data-testid="browse-products-button">
                Browse Products
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 md:py-16 lg:py-24" data-testid="cart-page">
      <div className="container-custom">
        {/* Mobile Back Button */}
        <Link to="/products" className="inline-flex items-center text-muted-foreground hover:text-primary mb-4 md:hidden">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Continue Shopping
        </Link>

        <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold mb-6 md:mb-12 text-center" data-testid="cart-heading">
          Your Cart
          <span className="text-base md:text-lg font-normal text-muted-foreground ml-2">({cart.length} items)</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3 md:space-y-4">
            {cart.map((item) => (
              <div
                key={`${item.product_id}-${item.variant_size}`}
                className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-card"
                data-testid={`cart-item-${item.product_id}`}
              >
                <div className="flex gap-3 md:gap-6">
                  {/* Image */}
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.product_name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-grow min-w-0">
                    <h3 className="text-base md:text-xl font-heading font-semibold mb-1 truncate">
                      {item.product_name}
                    </h3>
                    <p className="text-xs md:text-sm text-muted-foreground mb-2">Size: {item.variant_size}</p>
                    <p className="text-lg md:text-2xl font-bold text-primary">₹{item.price}</p>
                  </div>

                  {/* Mobile: Remove Button */}
                  <button
                    data-testid={`remove-item-${item.product_id}`}
                    onClick={() => removeFromCart(item.product_id, item.variant_size)}
                    className="text-destructive hover:text-destructive/80 transition-colors p-1 self-start md:hidden"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                {/* Quantity Controls - Full width on mobile */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border md:border-0 md:pt-0 md:mt-0 md:absolute md:right-6 md:bottom-6">
                  <div className="flex items-center space-x-3">
                    <button
                      data-testid={`decrease-quantity-${item.product_id}`}
                      onClick={() => updateQuantity(item.product_id, item.variant_size, item.quantity - 1)}
                      className="w-9 h-9 md:w-8 md:h-8 rounded-full border-2 border-border hover:border-primary transition-colors flex items-center justify-center"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-lg font-semibold w-8 text-center" data-testid={`quantity-${item.product_id}`}>
                      {item.quantity}
                    </span>
                    <button
                      data-testid={`increase-quantity-${item.product_id}`}
                      onClick={() => updateQuantity(item.product_id, item.variant_size, item.quantity + 1)}
                      className="w-9 h-9 md:w-8 md:h-8 rounded-full border-2 border-border hover:border-primary transition-colors flex items-center justify-center"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Item Total on mobile */}
                  <span className="font-semibold text-lg md:hidden">
                    ₹{(item.price * item.quantity).toFixed(2)}
                  </span>

                  {/* Desktop: Remove Button */}
                  <button
                    onClick={() => removeFromCart(item.product_id, item.variant_size)}
                    className="text-destructive hover:text-destructive/80 transition-colors hidden md:block"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-8 shadow-card sticky top-20" data-testid="order-summary">
              <h2 className="text-xl md:text-2xl font-heading font-bold mb-4 md:mb-6">Order Summary</h2>
              
              <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                <div className="flex justify-between text-base md:text-lg">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold" data-testid="subtotal">₹{getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base md:text-lg">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="font-semibold text-green-600">Free</span>
                </div>
                <div className="border-t border-border pt-3 md:pt-4">
                  <div className="flex justify-between text-xl md:text-2xl font-bold">
                    <span>Total</span>
                    <span className="text-primary" data-testid="total">₹{getCartTotal().toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Inclusive of all taxes</p>
                </div>
              </div>

              <button
                data-testid="proceed-checkout-button"
                onClick={() => navigate('/checkout')}
                className="btn-accent w-full text-base md:text-lg py-4"
              >
                Proceed to Checkout
              </button>

              <Link to="/products" className="hidden md:block">
                <button className="btn-outline w-full mt-4" data-testid="continue-shopping-button">
                  Continue Shopping
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
